import React from "react";
import { Trans } from "react-i18next";

import {
  Callout,
  Button,
  InputGroup,
  Tag,
  Dialog,
  Classes,
  FormGroup,
  Checkbox,
  ButtonGroup,
} from "@blueprintjs/core";

import {
  authenticatedPutFetch,
  authenticatedPostFetch,
  authenticatedDeleteFetch,
} from "../../../utils/AuthenticatedFetch";
import EquipmentListSelect from "../../Structure/EquipmentSelector";
import ParkListSelect from "../../Structure/ParkSelector";
import { CountersSelect } from "../../Supervision/Counters";
import { cachedGroups } from "../../Supervision/SupervisionMenu";
import i18next from "i18next";
import { RemoteIosSelect } from "../../Supervision/RemoteIO/RemoteIO";

export default class GroupTable extends React.Component {
  constructor() {
    super();
    this.state = {
      groups: [],
      servers: [],
      parks: [],
      mainCounter: {},
      counters: [],
      creating: false,
      editing: -1,
      input_name: "",
      input_display: true,
      input_counters: [],
      input_mainCounter: {},
    };
  }

  componentDidMount() {
    this.updateTable();
  }

  async updateTable() {
    const groups = await cachedGroups(true);
    this.setState({ groups: groups });
  }

  getConcatInputCounters() {
    if (
      this.state.input_counters.length > 0 &&
      this.state.input_mainCounter?.id !== undefined
    )
      return this.state.input_counters.concat([this.state.input_mainCounter]);
    else if (this.state.input_counters.length === 0) {
      if (this.state.input_mainCounter?.id === undefined) return [];
      else return [this.state.input_mainCounter];
    } else {
      return this.state.input_counters;
    }
  }

  render() {
    const groups = [];

    for (const group of Object.values(this.state.groups)) {
      groups.push(
        <Callout
          title={
            <span>
              <ButtonGroup minimal={true}>
                <Button
                  icon="edit"
                  onClick={() =>
                    this.setState({
                      editing: group.id,
                      input_name: group.label,
                      input_display: group.display,
                      input_parks: group.parks,
                      input_devices: group.devices,
                      input_counters: group.counters ?? [],
                      input_mainCounter: group.mainCounter ?? {},
                      input_remote_inputs: group.remote_inputs ?? [],
                      input_remote_outputs: group.remote_outputs ?? [],
                    })
                  }
                />
                <Button
                  icon="trash"
                  onClick={() => this.setState({ deleting: group.id })}
                />
              </ButtonGroup>
              {group.label}
            </span>
          }
          key={"gr" + group.id}
        >
          <span>
            {group.display ? (
              <Trans>Visible as supervision page</Trans>
            ) : (
              <Trans>Not visible as supervision page</Trans>
            )}
          </span>
          <Callout title={<Trans>devices</Trans>}>
            {group.devices.map((device, index) => (
              <Tag key={index}>{device.name}</Tag>
            ))}
          </Callout>

          <Callout title={<Trans>parks</Trans>}>
            {group.parks.map((park, index) => (
              <Tag key={index}>{park.name}</Tag>
            ))}
          </Callout>

          <Callout title={<Trans>main counter</Trans>}>
            {group.mainCounter?.id ? (
              <Tag
                key={group.mainCounter.id}
              >{`${group.mainCounter.name} (parc: ${group.mainCounter.park}, srv: ${group.mainCounter.server})`}</Tag>
            ) : undefined}
          </Callout>

          <Callout title={<Trans>counters</Trans>}>
            {group.counters?.map((counter, index) => (
              <Tag
                key={index}
              >{`${counter.name} (parc: ${counter.park}, srv: ${counter.server})`}</Tag>
            ))}
          </Callout>

          {(group.remote_inputs.length > 0 ||
            group.remote_outputs.length > 0) && (
            <Callout title={<Trans>remote I/O</Trans>}>
              {group.remote_inputs.length > 0 && (
                <Callout title={<Trans>inputs</Trans>}>
                  <div className="flexbox-left">
                    {group.remote_inputs.map((io, index) => (
                      <Tag
                        key={index}
                      >{`${io.label} (paxetor n° ${io.input_id}, srv: ${io?.server?.name})`}</Tag>
                    ))}
                  </div>
                </Callout>
              )}
              {group.remote_outputs.length > 0 && (
                <Callout title={<Trans>outputs</Trans>}>
                  <div className="flexbox-left">
                    {group.remote_outputs.map((io, index) => (
                      <Tag
                        key={index}
                      >{`${io.label} (paxstor n° ${io.output_id}, srv: ${io?.server?.name})`}</Tag>
                    ))}
                  </div>
                </Callout>
              )}
            </Callout>
          )}
        </Callout>
      );
    }

    return (
      <Callout title={<Trans>supervison pages</Trans>}>
        <Button
          icon="add"
          text={<Trans>add a page</Trans>}
          onClick={() => this.setState({ creating: true })}
        />
        <Callout intent="primary">
          <Trans>
            these groups allow to manage display of supervision pages and user
            permission
          </Trans>
        </Callout>
        {/* creating dialog */}
        <Dialog
          style={{ transition: "none" }}
          isOpen={this.state.creating}
          onClose={() =>
            this.setState({ creating: false, creating_error: false })
          }
          title={<Trans>add a page</Trans>}
        >
          <div className={Classes.DIALOG_BODY}>
            <form
              onSubmit={(e) => {
                e.preventDefault(); // to avoid reload of the page on submit
                authenticatedPostFetch(
                  "/supervision/group",
                  {
                    label: this.state.input_name,
                  },
                  () =>
                    this.setState(
                      { creating: false, creating_error: false },
                      () => {
                        this.updateTable();
                        cachedGroups(true);
                      }
                    )
                );
              }}
            >
              <FormGroup
                label={<Trans>label</Trans>}
                labelFor="input_name"
                inline={true}
              >
                <InputGroup
                  id="input_name"
                  onChange={(ti) =>
                    this.setState({ input_name: ti.target.value })
                  }
                />
              </FormGroup>
              <Button text="OK" intent="primary" type="submit" icon="add" />
            </form>
          </div>
        </Dialog>
        {groups}

        {/* editing dialog */}
        {this.state.editing > 0 && (
          <Dialog
            style={{ transition: "none" }}
            isOpen={this.state.editing >= 0}
            onClose={() =>
              this.setState({ editing: -1, creating_error: false })
            }
            title={<Trans>edit group</Trans>}
          >
            <div className={Classes.DIALOG_BODY}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  authenticatedPutFetch(
                    "/supervision/group/" + this.state.editing,
                    {
                      label: this.state.input_name,
                      display: this.state.input_display,
                      devices: this.state.input_devices,
                      parks: this.state.input_parks,
                      counters: this.state.input_counters,
                      mainCounter: this.state.input_mainCounter,
                      remote_inputs: this.state.input_remote_inputs,
                      remote_outputs: this.state.input_remote_outputs,
                    },
                    () =>
                      this.setState(
                        { editing: -1, creating_error: false },
                        () => {
                          this.updateTable();
                          cachedGroups(true);
                        }
                      ),
                    () => this.setState({ creating_error: true })
                  );
                }}
              >
                <FormGroup
                  label={<Trans>name</Trans>}
                  labelFor="input_name"
                  inline={true}
                >
                  <InputGroup
                    id="input_name"
                    intent={this.state.creating_error ? "danger" : "normal"}
                    onChange={(ti) =>
                      this.setState({ input_name: ti.target.value })
                    }
                    value={this.state.input_name}
                  />
                </FormGroup>

                <Callout title={<Trans>parks</Trans>}>
                  <ParkListSelect
                    label={i18next.t("add a park")}
                    onSelect={(parkId, park) =>
                      this.setState({
                        input_parks: [...this.state.input_parks, park],
                      })
                    }
                  />
                  <div className="flexbox-left">
                    {this.state.input_parks.map((park, index) => (
                      <Tag
                        key={index}
                        onRemove={() =>
                          this.setState({
                            input_parks: this.state.input_parks.filter(
                              (i) => i !== park
                            ),
                          })
                        }
                      >
                        {park.name}
                      </Tag>
                    ))}
                  </div>
                </Callout>

                <Callout title={<Trans>devices</Trans>}>
                  <EquipmentListSelect
                    label={i18next.t("add device")}
                    valueSelected={this.state.input_devices}
                    onSelect={(deviceId, device) =>
                      this.setState({
                        input_devices: [...this.state.input_devices, device],
                      })
                    }
                  />

                  <div className="flexbox-left">
                    {this.state.input_devices.map((device, index) => (
                      <Tag
                        key={index}
                        onRemove={() =>
                          this.setState({
                            input_devices: this.state.input_devices.filter(
                              (i) => i !== device
                            ),
                          })
                        }
                      >
                        {device.name}
                      </Tag>
                    ))}
                  </div>
                </Callout>

                <Callout title={<Trans>counters</Trans>}>
                  <CountersSelect
                    label="select a main counter"
                    valueSelected={this.getConcatInputCounters()}
                    onSelect={(counterId, counter) =>
                      this.setState({ input_mainCounter: counter })
                    }
                  />
                  <div className="flexbox-left">
                    {this.state.input_mainCounter?.id !== undefined ? (
                      <Tag
                        key={`main_counter_${this.state.input_mainCounter.id}`}
                        onRemove={() =>
                          this.setState({ input_mainCounter: {} })
                        }
                      >{`${this.state.input_mainCounter.name} (parc: ${this.state.input_mainCounter.park}, srv: ${this.state.input_mainCounter.server})`}</Tag>
                    ) : undefined}
                  </div>

                  <CountersSelect
                    label="add a counter"
                    valueSelected={this.getConcatInputCounters()}
                    onSelect={(counterId, counter) =>
                      this.setState({
                        input_counters: [...this.state.input_counters, counter],
                      })
                    }
                  />
                  <div className="flexbox-left">
                    {this.state.input_counters.map((counter, index) => (
                      <Tag
                        key={index}
                        onRemove={() => {
                          const ret = this.state.input_counters.filter(
                            (i) => i.id !== counter.id
                          );
                          this.setState({ input_counters: ret });
                        }}
                      >{`${counter.name} (parc: ${counter.park}, srv: ${counter.server})`}</Tag>
                    ))}
                  </div>
                </Callout>

                <Callout title={<Trans>remote I/O</Trans>}>
                  <Callout title={<Trans>inputs</Trans>}>
                    <RemoteIosSelect
                      label="add remote input"
                      type="INPUT"
                      valueSelected={this.state.input_remote_inputs}
                      onSelect={(ioId, io) =>
                        this.setState({
                          input_remote_inputs: [
                            ...this.state.input_remote_inputs,
                            io,
                          ],
                        })
                      }
                    />
                    <div className="flexbox-left">
                      {this.state.input_remote_inputs.map((io, index) => (
                        <Tag
                          key={index}
                          onRemove={() => {
                            const ret = this.state.input_remote_inputs.filter(
                              (i) => i.id !== io.id
                            );
                            this.setState({ input_remote_inputs: ret });
                          }}
                        >{`${io.label} (paxetor n° ${io.input_id}, srv: ${io?.server?.name})`}</Tag>
                      ))}
                    </div>
                  </Callout>
                  <Callout title={<Trans>outputs</Trans>}>
                    <RemoteIosSelect
                      label="add remote output"
                      type="OUTPUT"
                      valueSelected={this.state.input_remote_outputs}
                      onSelect={(ioId, io) =>
                        this.setState({
                          input_remote_outputs: [
                            ...this.state.input_remote_outputs,
                            io,
                          ],
                        })
                      }
                    />
                    <div className="flexbox-left">
                      {this.state.input_remote_outputs.map((io, index) => (
                        <Tag
                          key={index}
                          onRemove={() => {
                            const ret = this.state.input_remote_outputs.filter(
                              (i) => i.id !== io.id
                            );
                            this.setState({ input_remote_outputs: ret });
                          }}
                        >{`${io.label} (paxstor n° ${io.output_id}, srv: ${io?.server?.name})`}</Tag>
                      ))}
                    </div>
                  </Callout>
                </Callout>

                <FormGroup
                  label={<Trans>display</Trans>}
                  labelFor="input_display"
                  inline={true}
                >
                  <Checkbox
                    id="input_display"
                    onChange={(ti) =>
                      this.setState({ input_display: ti.target.checked })
                    }
                    checked={this.state.input_display}
                  />
                </FormGroup>

                <Button
                  text={<Trans>edit</Trans>}
                  intent="primary"
                  icon="edit"
                  type="input"
                />
              </form>
            </div>
          </Dialog>
        )}

        {/* deleting dialog */}
        {this.state.deleting > 0 && (
          <Dialog
            style={{ transition: "none" }}
            isOpen={this.state.deleting >= 0}
            onClose={() => this.setState({ deleting: -1 })}
            title={<Trans>delete group</Trans>}
          >
            <div className={Classes.DIALOG_BODY}>
              <Trans>this operation is irreversible</Trans>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                <Button
                  text={<Trans>cancel</Trans>}
                  icon="undo"
                  onClick={() => this.setState({ deleting: -1 })}
                />
                <Button
                  text={<Trans>delete</Trans>}
                  intent="danger"
                  icon="trash"
                  onClick={() =>
                    authenticatedDeleteFetch(
                      "/supervision/group/" + this.state.deleting,
                      null,
                      () =>
                        this.setState({ deleting: -1 }, () => {
                          this.updateTable();
                          cachedGroups(true);
                        }),
                      () => this.setState({ deleting: -1 })
                    )
                  }
                />
              </div>
            </div>
          </Dialog>
        )}
      </Callout>
    );
  }
}
